import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/i18n/I18nProvider";
import { supabase } from "@/integrations/supabase/client";
import { supabaseUntyped } from "@/integrations/supabase/untyped";
import { Sparkles, Upload, Trash2, Info } from "lucide-react";
import { parseBulkPaste } from "@/lib/bulkPasteParser";
import type { ExternalBookingRow, ExternalSchedulingMode } from "./types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  defaultServiceLabel?: string;
  onImported?: (rows: ExternalBookingRow[]) => void;
}

interface DraftRow {
  id: string;
  address: string;
  title: string;
  serviceLabel: string;
  scheduling_mode: ExternalSchedulingMode;
  checkoutAt: Date | null; // window mode
  checkinAt: Date | null;
  checkoutDate: string;    // date_only
  checkinDate: string;
  guests: number | null;
  hasPets: boolean | null;
  hasKids: boolean | null;
  notes: string | null;
  errors: string[];
}

const MAX_ROWS = 50;

export default function ExternalBookingsBulkAdd({ open, onOpenChange, companyId, defaultServiceLabel, onImported }: Props) {
  const { toast } = useToast();
  const { t } = useI18n();
  const [rawText, setRawText] = useState("");
  const [defaults, setDefaults] = useState({
    checkoutTime: "11:00",
    checkinTime: "15:00",
    bufferMinutes: 0,
    serviceLabel: defaultServiceLabel || "Pulizia",
    schedulingMode: "window" as ExternalSchedulingMode,
  });
  const [drafts, setDrafts] = useState<DraftRow[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const parseNow = () => {
    const parsed = parseBulkPaste(rawText, {
      defaultCheckoutTime: defaults.checkoutTime,
      defaultCheckinTime: defaults.checkinTime,
      bufferMinutes: defaults.bufferMinutes,
      maxRows: MAX_ROWS,
    });
    const rows: DraftRow[] = parsed.rows.map((r) => {
      const errors: string[] = [];
      const hasTimes = !!(r.checkoutAt && r.checkinAt);
      const mode: ExternalSchedulingMode = defaults.schedulingMode;
      if ((mode === "window" || mode === "date_only") && !hasTimes) {
        errors.push("Date checkout/check-in mancanti.");
      }
      return {
        id: r.id,
        address: r.address || "",
        title: "",
        serviceLabel: defaults.serviceLabel,
        scheduling_mode: mode,
        checkoutAt: r.checkoutAt,
        checkinAt: r.checkinAt,
        checkoutDate: r.checkoutAt ? r.checkoutAt.toISOString().slice(0, 10) : "",
        checkinDate: r.checkinAt ? r.checkinAt.toISOString().slice(0, 10) : "",
        guests: r.guests,
        hasPets: r.hasPets,
        hasKids: r.hasKids,
        notes: r.notes,
        errors,
      };
    });
    setDrafts(rows);
    if (parsed.truncated) {
      toast({ title: "Troppe righe", description: `Mostrate solo le prime ${MAX_ROWS}.`, variant: "destructive" });
    }
  };

  const updateDraft = (id: string, patch: Partial<DraftRow>) => {
    setDrafts((rows) =>
      rows.map((r) => {
        if (r.id !== id) return r;
        const next = { ...r, ...patch };
        const errors: string[] = [];
        if (!next.address.trim() && !next.title.trim()) errors.push("Indirizzo o etichetta obbligatori.");
        if (next.scheduling_mode === "window") {
          if (!next.checkoutAt || !next.checkinAt) errors.push("Checkout/check-in con orario richiesti.");
          else if (next.checkinAt.getTime() <= next.checkoutAt.getTime()) errors.push("Check-in deve essere dopo il checkout.");
        }
        if (next.scheduling_mode === "date_only") {
          if (!next.checkoutDate || !next.checkinDate) errors.push("Giorni checkout/check-in richiesti.");
          else if (new Date(next.checkinDate).getTime() < new Date(next.checkoutDate).getTime())
            errors.push("Giorno check-in non valido.");
        }
        next.errors = errors;
        return next;
      })
    );
  };

  const removeDraft = (id: string) => setDrafts((rows) => rows.filter((r) => r.id !== id));

  const validRows = useMemo(() => drafts.filter((d) => d.errors.length === 0), [drafts]);

  const handleSubmit = async () => {
    if (validRows.length === 0) {
      toast({ title: "Nulla da importare", description: "Correggi gli errori o aggiungi righe valide.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const userId = sess?.session?.user?.id;
      if (!userId) {
        toast({ title: t("vendor.tracker.session_expired"), variant: "destructive" });
        return;
      }
      const payloads = validRows.map((r) => buildPayload(r, companyId, userId));
      const { data, error } = await supabaseUntyped
        .from("external_bookings")
        .insert(payloads)
        .select("*");
      if (error) throw error;
      const rows = (data || []) as ExternalBookingRow[];
      toast({ title: "Importazione completata", description: `${rows.length} booking esterni aggiunti al tracker.` });
      onImported?.(rows);
      setDrafts([]);
      setRawText("");
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Riprova.";
      toast({ title: t("vendor.tracker.import_error"), description: message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importa più booking esterni</DialogTitle>
          <DialogDescription>
            Incolla una lista di prenotazioni — un appartamento per riga. Vengono analizzati indirizzi,
            checkout, check-in, ospiti, animali. Tutto resta solo nel tracker, non genera commissioni.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="paste">
          <TabsList className="grid grid-cols-2 max-w-sm bg-slate-100/70">
            <TabsTrigger value="paste">1. Incolla</TabsTrigger>
            <TabsTrigger value="review" disabled={drafts.length === 0}>
              {t('vendor.tracker.review_save')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="paste" className="space-y-3">
            <div className="rounded-xl border border-blue-200 bg-blue-50/70 p-3 text-xs text-blue-900 flex gap-2">
              <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <div>
                  <strong>Cos'è il checkout e il check-in qui?</strong> Stai tracciando le finestre di pulizia tra
                  un ospite e l'altro:
                </div>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li><strong>Checkout</strong> = data e ora in cui gli <strong>ospiti attualmente in casa</strong> escono.</li>
                  <li><strong>Check-in</strong> = data e ora in cui i <strong>prossimi ospiti</strong> arrivano.</li>
                  <li>La <strong>pulizia</strong> va programmata in mezzo (tra checkout e check-in).</li>
                </ul>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Servizio (default)</Label>
                <Input
                  value={defaults.serviceLabel}
                  onChange={(e) => setDefaults((d) => ({ ...d, serviceLabel: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">Modalità default</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={defaults.schedulingMode}
                  onChange={(e) => setDefaults((d) => ({ ...d, schedulingMode: e.target.value as ExternalSchedulingMode }))}
                >
                  <option value="window">Checkout/Check-in con orari</option>
                  <option value="date_only">Solo giorni</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Ora checkout default</Label>
                  <Input
                    type="time"
                    value={defaults.checkoutTime}
                    onChange={(e) => setDefaults((d) => ({ ...d, checkoutTime: e.target.value }))}
                  />
                  <div className="text-[10px] text-slate-500 mt-0.5">Uscita ospiti attuali</div>
                </div>
                <div>
                  <Label className="text-xs">Ora check-in default</Label>
                  <Input
                    type="time"
                    value={defaults.checkinTime}
                    onChange={(e) => setDefaults((d) => ({ ...d, checkinTime: e.target.value }))}
                  />
                  <div className="text-[10px] text-slate-500 mt-0.5">Arrivo prossimi ospiti</div>
                </div>
              </div>
            </div>

            <Label className="text-xs">Incolla qui le righe (una per appartamento)</Label>
            <Textarea
              rows={8}
              placeholder={`Formato: indirizzo | uscita ospiti attuali | arrivo prossimi ospiti | ospiti/note\n\nEsempi:\nVia Roma 12, Milano | 25/04/2026 11:00 | 25/04/2026 15:00 | 4 pax cane\nApt. Centro Storico | 26-04-2026 | 27-04-2026 | 2 ospiti\nVia Po 5, Torino | 28/04/2026 11:00 | 28/04/2026 16:00`}
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
            />

            <div className="flex justify-end">
              <Button onClick={parseNow} disabled={!rawText.trim()}>
                <Sparkles className="h-4 w-4 mr-2" /> Analizza
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="review" className="space-y-3">
            <div className="text-xs text-muted-foreground">
              {drafts.length} righe analizzate · {validRows.length} valide ·{" "}
              <span className="text-red-600">{drafts.length - validRows.length} con errori</span>
            </div>
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {drafts.map((r) => (
                <div
                  key={r.id}
                  className={`rounded-xl border p-3 space-y-2 ${
                    r.errors.length ? "border-red-300 bg-red-50/40" : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[11px]">Etichetta</Label>
                        <Input value={r.title} onChange={(e) => updateDraft(r.id, { title: e.target.value })} />
                      </div>
                      <div>
                        <Label className="text-[11px]">Indirizzo</Label>
                        <Input value={r.address} onChange={(e) => updateDraft(r.id, { address: e.target.value })} />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 self-start pt-5">
                      {r.errors.length ? <Badge variant="destructive">Errori</Badge> : <Badge>OK</Badge>}
                      <Button size="sm" variant="outline" onClick={() => removeDraft(r.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <Label className="text-[11px]">Servizio</Label>
                      <Input
                        value={r.serviceLabel}
                        onChange={(e) => updateDraft(r.id, { serviceLabel: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="text-[11px]">Modalità</Label>
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={r.scheduling_mode}
                        onChange={(e) => updateDraft(r.id, { scheduling_mode: e.target.value as ExternalSchedulingMode })}
                      >
                        <option value="window">Checkout/Check-in (orari)</option>
                        <option value="date_only">Solo giorni</option>
                        <option value="exact">Orario esatto</option>
                        <option value="flexible">Flessibile</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-[11px]">Ospiti</Label>
                      <Input
                        type="number"
                        value={r.guests ?? ""}
                        onChange={(e) =>
                          updateDraft(r.id, { guests: e.target.value ? Number(e.target.value) : null })
                        }
                      />
                    </div>
                  </div>

                  {(r.scheduling_mode === "window" || r.scheduling_mode === "exact") && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[11px]">
                          {r.scheduling_mode === "exact" ? "Inizio pulizia" : "Checkout (uscita ospiti attuali)"}
                        </Label>
                        <Input
                          type="datetime-local"
                          value={r.checkoutAt ? toLocalDt(r.checkoutAt) : ""}
                          onChange={(e) =>
                            updateDraft(r.id, {
                              checkoutAt: e.target.value ? new Date(e.target.value) : null,
                            })
                          }
                        />
                      </div>
                      {r.scheduling_mode === "window" && (
                        <div>
                          <Label className="text-[11px]">Check-in (arrivo prossimi ospiti)</Label>
                          <Input
                            type="datetime-local"
                            value={r.checkinAt ? toLocalDt(r.checkinAt) : ""}
                            onChange={(e) =>
                              updateDraft(r.id, {
                                checkinAt: e.target.value ? new Date(e.target.value) : null,
                              })
                            }
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {r.scheduling_mode === "date_only" && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[11px]">Giorno checkout (uscita ospiti attuali)</Label>
                        <Input
                          type="date"
                          value={r.checkoutDate}
                          onChange={(e) => updateDraft(r.id, { checkoutDate: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-[11px]">Giorno check-in (arrivo prossimi ospiti)</Label>
                        <Input
                          type="date"
                          value={r.checkinDate}
                          onChange={(e) => updateDraft(r.id, { checkinDate: e.target.value })}
                        />
                      </div>
                    </div>
                  )}

                  {r.errors.length > 0 && (
                    <div className="text-[11px] text-red-700">
                      {r.errors.map((e, i) => (
                        <div key={i}>- {e}</div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Annulla
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || validRows.length === 0}>
            <Upload className="h-4 w-4 mr-2" />
            {submitting ? "Importazione…" : `Importa ${validRows.length} booking`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function toLocalDt(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function buildPayload(r: DraftRow, companyId: string, userId: string) {
  let start: Date | null = null;
  let end: Date | null = null;
  let allDay = false;
  if (r.scheduling_mode === "exact") {
    start = r.checkoutAt;
  } else if (r.scheduling_mode === "window") {
    start = r.checkoutAt;
    end = r.checkinAt;
  } else if (r.scheduling_mode === "date_only") {
    start = r.checkoutDate ? new Date(`${r.checkoutDate}T00:00:00`) : null;
    end = r.checkinDate ? new Date(`${r.checkinDate}T23:59:59`) : null;
    allDay = true;
  }
  return {
    company_id: companyId,
    created_by_user_id: userId,
    title: r.title.trim() || null,
    address: r.address.trim() || null,
    service_label: r.serviceLabel.trim() || null,
    scheduling_mode: r.scheduling_mode,
    start_at: start ? start.toISOString() : null,
    end_at: end ? end.toISOString() : null,
    all_day: allDay,
    guests: r.guests,
    has_pets: r.hasPets,
    has_kids: r.hasKids,
    notes: r.notes,
    status: "planned",
    currency: "EUR",
  };
}
